import React, { useMemo, useState } from 'react';
import i18n from '../../../localization/i18n';
import { vars } from '../../../Theme';
import ActionButton from '../../General/ActionButton/ActionButton';
import SelectedDocumentsDownloadButton, {
    getMenuButtonLabelSx,
    getPopoverPlacement,
    menuItemSx,
} from './SelectedDocumentsDownloadButton';
import ContextMenu from '../../General/ContextMenu/ContextMenu';
import ContextMenuItem from '../../General/ContextMenu/ContextMenuItem';
import { IProjectViewData } from '../../../redux/project/sliceProjectViews';
import { devOnClick, docTableActionsCallbacks } from '../../General/DocTable/DocTableActions';
import { IAvailableSnackbarAction } from '../../General/SnackbarActions/SnackbarActions';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { mdiMenuRight } from '@mdi/js';
import Icon from '@mdi/react';
import { defineActionButtonsAvailability, getToolTip } from '../../../util/DataUtils';
import AppTooltip from '../../General/AppTooltip/AppTooltip';
import { useTranslation } from 'react-i18next';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';

const getActionButtonSx = (isOpen: boolean) => {
    return {
        color: isOpen ? vars.brandGreen : vars.font,
        backgroundColor: isOpen ? vars.selectedBgGreen : vars.white,
    };
};

const getActionButtonIconColorSx = (isOpen: boolean, color: string) =>
    isOpen ? vars.brandGreen : color;

/**
 * Button Group Component that allows the user to choose other actions
 */
export const SelectedDocumentsMenuButton = ({
    actions,
    replicaId,
    documentIds,
    currentView,
    reportName,
    icon,
    text,
    color,
    isFromKebab = false,
    universalId,
    idDocIndex,
    fileName,
    document,
}: ISelectedDocumentsMenuButton) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });
    /** State slice for controlling the position of the button's menu. */
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
    const pendingActions = useSelector((store: IAppReduxStore) => store.actionHandling.actions);

    /**
     * Closes the context menu.
     */
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    /**
     * Makes the context menu visible.
     */
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        setAnchorEl(e.currentTarget as HTMLElement);
    };

    /**
     * Returns available items for creation actions menu.
     */
    const menuItems = useMemo(() => {
        if (!actions?.length) return null;
        return (
            actions
                .map(action => {
                    const { name, icon, color, translations, groupActions, extendedConfig } =
                        action;
                    const { maxDocs, minDocs } = extendedConfig ?? { maxDocs: 0, minDocs: 0 };
                    if (groupActions?.length) {
                        return (
                            <SelectedDocumentsMenuButton
                                key={name}
                                actions={groupActions}
                                icon={icon ?? ''}
                                text={translations[i18n.language] ?? name}
                                color={color}
                                replicaId={replicaId}
                                documentIds={documentIds}
                                currentView={currentView}
                                reportName={reportName}
                                isFromKebab={true}
                                document={document}
                            />
                        );
                    }

                    if (name === 'download') {
                        return (
                            <SelectedDocumentsDownloadButton
                                key={name}
                                icon={icon}
                                maxDocs={maxDocs}
                                minDocs={minDocs}
                                text={translations[i18n.language]}
                                isFromKebab={true}
                                onClose={handleCloseMenu}
                            />
                        );
                    } else {
                        const isActionButtonDisabled =
                            defineActionButtonsAvailability(
                                documentIds?.length,
                                maxDocs,
                                minDocs,
                            ) || (pendingActions ?? []).includes(name);
                        return (
                            <AppTooltip
                                key={name}
                                title={
                                    isActionButtonDisabled
                                        ? t(getToolTip(maxDocs, minDocs), { maxDocs, minDocs })
                                        : null
                                }
                                placement='top-start'
                            >
                                <Box>
                                    <ContextMenuItem
                                        key={name}
                                        text={translations[i18n.language]}
                                        icon={icon}
                                        color={color}
                                        disabled={isActionButtonDisabled}
                                        onClick={() => {
                                            (extendedConfig?.dynamicAction
                                                ? docTableActionsCallbacks.dynamicAction
                                                : docTableActionsCallbacks[name] ?? devOnClick)({
                                                replicaId,
                                                documentIds,
                                                currentView,
                                                reportName,
                                                universalId,
                                                idDocIndex,
                                                fileName,
                                                actionKey: name,
                                                document,
                                            });

                                            handleCloseMenu();
                                        }}
                                    />
                                </Box>
                            </AppTooltip>
                        );
                    }
                })
                /** Remove any null items from the array. */
                .filter(Boolean)
        );
    }, [actions, pendingActions]);

    return (
        <>
            {isFromKebab ? (
                <MenuItem key={text} sx={menuItemSx} selected={!!anchorEl} onClick={handleClick}>
                    <Icon color={color} path={icon} size={1} />
                    <Typography sx={getMenuButtonLabelSx(!!anchorEl)}>{text}</Typography>
                    <Stack
                        direction='row'
                        sx={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 4 }}
                    >
                        <Icon path={mdiMenuRight} size={1} />
                    </Stack>
                </MenuItem>
            ) : (
                <ActionButton
                    sx={getActionButtonSx(!!anchorEl)}
                    icon={icon}
                    text={text}
                    onClick={handleClick}
                    color={getActionButtonIconColorSx(!!anchorEl, color)}
                />
            )}
            <ContextMenu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleCloseMenu}
                {...getPopoverPlacement(isFromKebab)}
            >
                {menuItems}
            </ContextMenu>
        </>
    );
};

export interface ISelectedDocumentsMenuButton {
    actions: IAvailableSnackbarAction[];
    replicaId: string;
    text: string;
    documentIds?: string[];
    currentView?: IProjectViewData;
    reportName?: string;
    universalId?: string;
    idDocIndex?: string;
    fileName?: string;
    icon?: string;
    color?: string;
    isFromKebab?: boolean;
    document?: IDocDefaultFields;
}
